import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { isAuthRequired } from '@/lib/environment';
import { FamilyMember } from '@/types/familyMember';
import { FamilyMemberEmail, dbRowToFamilyMemberEmail } from '@/types/familyMemberEmail';
import { useNicknames } from './useNicknames';
import { toast } from '@/lib/toast';

const dbRowToMember = (row: any, emails: FamilyMemberEmail[] = []): FamilyMember => ({
  id: row.id,
  name: row.name,
  currentStreak: row.current_streak ?? 0,
  longestStreak: row.longest_streak ?? 0,
  lastActiveDate: row.last_active_date ? new Date(row.last_active_date) : null,
  email: row.email || null,
  userId: row.user_id || null,
  emails: emails.filter(e => e.familyMemberId === row.id),
  createdAt: new Date(row.created_at),
  teamId: row.team_id || null,
  sortOrder: row.sort_order ?? 0,
  isVisible: row.is_visible ?? true,
});

const deserializeMemberFromCache = (row: any): FamilyMember => {
  const createdAt = row.createdAt ?? row.created_at;
  return {
    id: row.id,
    name: row.name,
    currentStreak: row.currentStreak ?? row.current_streak ?? 0,
    longestStreak: row.longestStreak ?? row.longest_streak ?? 0,
    lastActiveDate: row.lastActiveDate ? new Date(row.lastActiveDate) : (row.last_active_date ? new Date(row.last_active_date) : null),
    email: row.email ?? null,
    userId: row.userId ?? row.user_id ?? null,
    emails: (row.emails || []).map((e: any) => ({
      ...e,
      createdAt: e.createdAt ? new Date(e.createdAt) : new Date(),
    })),
    createdAt: createdAt ? new Date(createdAt) : new Date(),
    teamId: row.teamId ?? row.team_id ?? null,
    sortOrder: row.sortOrder ?? row.sort_order ?? 0,
    isVisible: row.isVisible ?? row.is_visible ?? true,
  };
};

const FAMILY_CACHE_PREFIX = 'team-v-does-family-cache:';
const FAMILY_DEV_CACHE_PREFIX = 'team-v-does-family-cache-dev:';
const DEV_IMPERSONATE_MEMBER_KEY = 'team-v-does-dev-impersonate-member-id';

const getFamilyCacheKey = (teamId: string) => `${FAMILY_CACHE_PREFIX}${teamId}`;
const getFamilyDevCacheKey = (teamId: string) => `${FAMILY_DEV_CACHE_PREFIX}${teamId}`;

const loadFamilyFromCache = (teamId: string): FamilyMember[] => {
  if (isAuthRequired()) {
    try {
      const lastUserId = localStorage.getItem('team-v-does-last-user-id');
      if (!lastUserId) return [];
    } catch {
      return [];
    }
  }
  try {
    const raw = localStorage.getItem(getFamilyCacheKey(teamId));
    if (!raw && !isAuthRequired()) {
      const devRaw = localStorage.getItem(getFamilyDevCacheKey(teamId));
      if (!devRaw) return [];
      return JSON.parse(devRaw).map((row: any) => deserializeMemberFromCache(row));
    }
    if (!raw) return [];
    return JSON.parse(raw).map((row: any) => deserializeMemberFromCache(row));
  } catch {
    return [];
  }
};

const saveFamilyToCache = (teamId: string, members: FamilyMember[]) => {
  try {
    const payload = members.map((m) => ({
      ...m,
      createdAt: m.createdAt.toISOString(),
      lastActiveDate: m.lastActiveDate ? m.lastActiveDate.toISOString() : null,
    }));
    localStorage.setItem(getFamilyCacheKey(teamId), JSON.stringify(payload));
    if (!isAuthRequired()) {
      localStorage.setItem(getFamilyDevCacheKey(teamId), JSON.stringify(payload));
    }
  } catch {
    // ignore
  }
};

export function useFamilyMembers(teamId: string | null, authUserId?: string | null, authUserEmail?: string | null) {
  const [members, setMembers] = useState<FamilyMember[]>(() => (teamId ? loadFamilyFromCache(teamId) : []));
  const [loading, setLoading] = useState(() => (teamId ? loadFamilyFromCache(teamId).length === 0 : true));
  const [memberAvatars, setMemberAvatars] = useState<Record<string, string | null>>({});
  const [devImpersonateId, setDevImpersonateId] = useState<string | null>(() => {
    if (isAuthRequired()) return null;
    try {
      return localStorage.getItem(DEV_IMPERSONATE_MEMBER_KEY);
    } catch {
      return null;
    }
  });

  const fetchMembers = useCallback(async () => {
    if (!teamId) {
      setMembers([]);
      setLoading(false);
      return;
    }

    // Fetch family members and their emails in parallel
    const [membersResult, emailsResult] = await Promise.all([
      supabase
        .from('family_members')
        .select('*')
        .eq('team_id', teamId)
        .eq('is_visible', true)
        .order('sort_order'),
      supabase
        .from('family_member_emails')
        .select('*')
        .eq('team_id', teamId)
        .order('is_primary', { ascending: false })
        .order('created_at'),
    ]);

    if (membersResult.error) {
      console.error('Error fetching members:', membersResult.error);
      if (!isAuthRequired()) {
        const cached = loadFamilyFromCache(teamId);
        if (cached.length > 0) {
          setMembers(cached);
        }
      }
      setLoading(false);
      return;
    }

    // Parse emails
    const emails: FamilyMemberEmail[] = emailsResult.data?.map(dbRowToFamilyMemberEmail) || [];

    // Build members with their emails
    const nextMembers = membersResult.data?.map(row => dbRowToMember(row, emails)) || [];
    setMembers(nextMembers);
    saveFamilyToCache(teamId, nextMembers);
    setLoading(false);

    // Fetch avatars for all members that have a linked userId
    const userIds = nextMembers
      .map(m => m.userId)
      .filter((id): id is string => Boolean(id));
    if (userIds.length > 0) {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, avatar_url')
        .in('id', userIds);
      if (profiles) {
        const avatarMap: Record<string, string | null> = {};
        // map by userId → avatar_url
        profiles.forEach(p => { avatarMap[p.id] = p.avatar_url ?? null; });
        // now convert to memberId → avatar_url
        const memberAvatarMap: Record<string, string | null> = {};
        nextMembers.forEach(m => {
          if (m.userId && avatarMap[m.userId] !== undefined) {
            memberAvatarMap[m.id] = avatarMap[m.userId];
          }
        });
        setMemberAvatars(memberAvatarMap);
      }
    }
  }, [teamId]);

  useEffect(() => {
    if (teamId) {
      const cached = loadFamilyFromCache(teamId);
      if (cached.length > 0) {
        setMembers(cached);
        setLoading(false);
      }
    }
    fetchMembers();

    const channel = supabase
      .channel('family-members-realtime')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'family_members' },
        () => {
          fetchMembers();
        }
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'family_member_emails' },
        () => {
          fetchMembers();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchMembers, teamId]);

  // Current user: authenticated user wins if matched to a member.
  // Falls back to dev impersonation only if no auth match is found (on dev).
  const currentUser = (() => {
    // 1. Try to find member by auth userId (strongest match)
    if (authUserId) {
      const matched = members.find((m) =>
        m.userId === authUserId ||
        m.emails.some(e => e.userId === authUserId)
      );
      if (matched) return matched;

      // 2. Best-effort: Try to find member by current user's email if userId not linked yet
      // This is helpful for the very first login before the DB link is written.
      if (authUserEmail) {
        const normalizedEmail = authUserEmail.toLowerCase();
        const emailMatched = members.find((m) =>
          m.email?.toLowerCase() === normalizedEmail ||
          m.emails.some(e => e.email.toLowerCase() === normalizedEmail)
        );
        if (emailMatched) return emailMatched;
      }
    }

    // 3. Dev impersonation fallback
    if (!isAuthRequired() && devImpersonateId) {
      return members.find((m) => m.id === devImpersonateId) || null;
    }

    return null;
  })();

  const currentUserId = currentUser?.id || null;

  // Link a family member to a user_id
  const linkMemberToUser = useCallback(async (memberId: string, userId: string) => {
    const { error } = await supabase
      .from('family_members')
      .update({ user_id: userId })
      .eq('id', memberId);

    if (error) {
      console.error('Error linking member to user:', error);
      return false;
    }

    setMembers(prev =>
      prev.map(m => m.id === memberId ? { ...m, userId } : m)
    );
    return true;
  }, []);

  // setCurrentUserId: dev impersonation, auth-driven in prod (but allows initial "claim")
  const setCurrentUserId = useCallback((_id: string | null) => {
    if (isAuthRequired()) {
      // If we are authenticated but not linked to any member, allow clicking to link (claim identity)
      if (authUserId && !currentUserId && _id) {
        toast.promise(linkMemberToUser(_id, authUserId), {
          loading: 'Identiteit koppelen...',
          success: 'Je bent nu gekoppeld!',
          error: 'Koppelen mislukt. Probeer later opnieuw.'
        }, { channel: 'teams' });
      } else {
        console.warn('setCurrentUserId ignored - user already linked or no ID provided');
      }
      return;
    }
    const nextId = _id || null;
    setDevImpersonateId(nextId);
    try {
      if (nextId) {
        localStorage.setItem(DEV_IMPERSONATE_MEMBER_KEY, nextId);
      } else {
        localStorage.removeItem(DEV_IMPERSONATE_MEMBER_KEY);
      }
    } catch {
      // ignore
    }
  }, [authUserId, currentUserId, linkMemberToUser]);

  const addMember = useCallback(async (name: string): Promise<string | null> => {
    if (!teamId) {
      toast.error('Selecteer eerst een team', { channel: 'teams' });
      return null;
    }

    const trimmedName = name.trim();
    if (!trimmedName) return null;

    const { data, error } = await supabase
      .from('family_members')
      .insert({ name: trimmedName, team_id: teamId })
      .select()
      .single();

    if (error) {
      if (error.code === '23505') {
        toast.error('Deze naam bestaat al', { channel: 'teams' });
      } else {
        console.error('Error adding member:', error);
        toast.error('Fout bij toevoegen van naam', { channel: 'teams' });
      }
      return null;
    }

    toast.success(`${trimmedName} toegevoegd`, { channel: 'teams' });
    return data.id;
  }, [teamId]);

  const updateMember = useCallback(async (id: string, updates: { name?: string; email?: string | null }) => {
    const dbUpdates: Record<string, any> = {};

    if (updates.name !== undefined) {
      const trimmedName = updates.name.trim();
      if (!trimmedName) return;
      dbUpdates.name = trimmedName;
    }

    if (updates.email !== undefined) {
      dbUpdates.email = updates.email?.trim() || null;
    }

    if (Object.keys(dbUpdates).length === 0) return;

    const { error } = await supabase
      .from('family_members')
      .update(dbUpdates)
      .eq('id', id);

    if (error) {
      console.error('Error updating member:', error);
      if (error.code === '23505') {
        toast.error('Dit e-mailadres is al in gebruik', { channel: 'teams' });
      } else {
        toast.error('Fout bij het bijwerken van lid', { channel: 'teams' });
      }
      return;
    }

    setMembers(prev =>
      prev.map(m => m.id === id ? { ...m, ...updates } : m)
    );
  }, []);


  // Find member by email (for auto-linking on login)
  // Now searches both legacy email field AND the new emails array
  const findMemberByEmail = useCallback((email: string): FamilyMember | undefined => {
    const normalizedEmail = email.toLowerCase();
    return members.find(m =>
      m.email?.toLowerCase() === normalizedEmail ||
      m.emails.some(e => e.email.toLowerCase() === normalizedEmail)
    );
  }, [members]);

  // Find member by user_id
  // Now searches both legacy userId field AND the new emails array
  const findMemberByUserId = useCallback((userId: string): FamilyMember | undefined => {
    return members.find(m =>
      m.userId === userId ||
      m.emails.some(e => e.userId === userId)
    );
  }, [members]);

  const reorderMembers = useCallback(async (reorderedMembers: FamilyMember[]) => {
    // Optimistically update local state
    setMembers(reorderedMembers);

    // Update each member's sort_order in the database
    const updates = reorderedMembers.map((member, index) =>
      supabase
        .from('family_members')
        .update({ sort_order: index })
        .eq('id', member.id)
    );

    const results = await Promise.all(updates);
    const hasError = results.some(r => r.error);

    if (hasError) {
      console.error('Error reordering members');
      toast.error('Fout bij het herordenen van leden', { channel: 'teams' });
      // Refetch to restore correct order
      fetchMembers();
    }
  }, [fetchMembers]);

  const deleteMember = useCallback(async (id: string) => {
    const { error } = await supabase
      .from('family_members')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting member:', error);
      toast.error('Fout bij het verwijderen van lid', { channel: 'teams' });
    }
  }, []);

  const { nicknames } = useNicknames(teamId);

  const getDisplayName = useCallback((targetMemberId: string, viewerMemberId?: string): string => {
    const target = members.find(m => m.id === targetMemberId);
    if (!target) return 'Onbekend';

    const viewer = viewerMemberId || currentUserId;
    if (!viewer) return target.name;

    const nicknameObj = nicknames.find(
      n => n.viewerId === viewer && n.targetId === targetMemberId
    );

    return nicknameObj ? nicknameObj.nickname : target.name;
  }, [members, currentUserId, nicknames]);

  const getMemberAvatarUrl = useCallback((memberId: string): string | null => {
    return memberAvatars[memberId] ?? null;
  }, [memberAvatars]);

  return {
    members,
    currentUser,
    currentUserId,
    setCurrentUserId,
    addMember,
    updateMember,
    deleteMember,
    reorderMembers,
    linkMemberToUser,
    findMemberByEmail,
    findMemberByUserId,
    getDisplayName,
    getMemberAvatarUrl,
    loading,
  };
}
